import { api } from '@/lib/api';
import type {
  BreakdownDim,
  BreakdownItem,
  DateRange,
  EventType,
  HeatmapCell,
  OverviewResponse,
  TimelinePoint,
} from '@/lib/types';

export interface IosMatchRate {
  iosClicks: number;
  iosInstalls: number;
  /** Ratio in [0, 1]; null when there are no iOS clicks in range. */
  rate: number | null;
}

export interface CampaignSparkline {
  code: string;
  /** Per-day click counts, oldest first; length = requested days. */
  clicks: number[];
  /** Per-day landing_click counts, oldest first; length = requested days. */
  landingClicks: number[];
}

export interface MatchedUser {
  userId: number;
  platform: string;
  targetPlatform: string | null;
  country: string | null;
  ip: string;
  ref: string | null;
  matchedAt: string;
  clickedAt: string;
}

const rangeParams = (r: DateRange) => ({ from: r.from, to: r.to });

export const statsApi = {
  // ---- Campaign-scoped ----------------------------------------------------

  campaignTimeline: (id: number, range: DateRange) =>
    api
      .get<TimelinePoint[]>(`/v1/campaigns/${id}/stats/timeline`, {
        params: rangeParams(range),
      })
      .then((r) => r.data),

  campaignBreakdown: (
    id: number,
    dim: BreakdownDim,
    type: EventType,
    range: DateRange,
  ) =>
    api
      .get<BreakdownItem[]>(`/v1/campaigns/${id}/stats/breakdown`, {
        params: { dim, type, ...rangeParams(range) },
      })
      .then((r) => r.data),

  campaignHeatmap: (id: number, type: EventType, range: DateRange) =>
    api
      .get<HeatmapCell[]>(`/v1/campaigns/${id}/stats/heatmap`, {
        params: { type, ...rangeParams(range) },
      })
      .then((r) => r.data),

  campaignIosMatchRate: (id: number, range: DateRange) =>
    api
      .get<IosMatchRate>(`/v1/campaigns/${id}/stats/ios-match-rate`, {
        params: rangeParams(range),
      })
      .then((r) => r.data),

  campaignSparklines: (days = 7, status = 'active') =>
    api
      .get<CampaignSparkline[]>('/v1/campaigns/sparklines', {
        params: { days, status },
      })
      .then((r) => r.data),

  campaignMatchedUsers: (id: number, limit = 50, offset = 0) =>
    api
      .get<MatchedUser[]>(`/v1/campaigns/${id}/matched-users`, {
        params: { limit, offset },
      })
      .then((r) => r.data),

  // ---- Tenant-wide --------------------------------------------------------

  overview: (range: DateRange) =>
    api
      .get<OverviewResponse>('/v1/stats/overview', { params: rangeParams(range) })
      .then((r) => r.data),

  tenantTimeline: (range: DateRange) =>
    api
      .get<TimelinePoint[]>('/v1/stats/timeline', { params: rangeParams(range) })
      .then((r) => r.data),

  tenantBreakdown: (dim: BreakdownDim, type: EventType, range: DateRange) =>
    api
      .get<BreakdownItem[]>('/v1/stats/breakdown', {
        params: { dim, type, ...rangeParams(range) },
      })
      .then((r) => r.data),

  tenantHeatmap: (type: EventType, range: DateRange) =>
    api
      .get<HeatmapCell[]>('/v1/stats/heatmap', {
        params: { type, ...rangeParams(range) },
      })
      .then((r) => r.data),
};
